// CouncilView.jsx — The Council (multi-agent deliberation), v2.
//
// Phases (named on the wire, legacy numeric accepted):
//   opinions → review → [rebuttal] → synthesis
// Essential mode skips rebuttal; rebuttal mode (default) inserts ONE rebuttal
// round before synthesis. Terminal status (complete/partial/cancelled/error) is
// independent of phase.
//
// Backend: POST /api/council/run (SSE) — see apolo-api/src/routes/council.js
// Reference: https://github.com/karpathy/llm-council
//
// Layering: this component is UI over three deep seams —
//   • useCouncilDeliberation — the run state machine (phases/terminal/progress)
//   • useCouncilHistory     — the cursor-paginated history data layer
//   • pure modules (councilProtocol/councilModel/councilExport/councilAttachments)

import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Crown, History as HistoryIcon, Settings2, Star } from 'lucide-react';
import { useFileAttachments } from '../../hooks/useFileAttachments.js';
import { CHAIRMAN_COLOR, CHAIRMAN_GLOW, COUNCIL_AGENTS, agentFromRegistryModel, getAgent } from '../../lib/councilAgents.js';
import { MIN_AGENTS, MAX_AGENTS } from '../../lib/councilClient.js';
import { findModel } from '../../lib/modelRegistry.js';
import { useUnifiedModels } from '../../hooks/useUnifiedModels.js';
import MobileDrawer from '../MobileDrawer.jsx';
import MarkdownMessage from '../MarkdownMessage.jsx';

import { useCouncilDeliberation } from './useCouncilDeliberation.js';
import { useCouncilHistory } from './useCouncilHistory.js';
import { deliberationToMarkdown, reusePayloadFromHistory, downloadCouncilMarkdown } from './councilExport.js';
import CouncilHistoryDetail from './CouncilHistoryDetail.jsx';
import CouncilComposer from './CouncilComposer.jsx';
import CouncilModelPicker from './CouncilModelPicker.jsx';
import CouncilStatus from './CouncilStatus.jsx';
import { CouncilHistoryList } from './CouncilHistory.jsx';
import { ExpandableOutput, SectionHeader } from './CouncilOutput.jsx';
import { ReviewMatrix, ReviewComparison } from './CouncilReview.jsx';

const PHASE_META = {
  opinions: { index: 1, label: 'Primeiras opiniões', color: 'var(--apolo-accent)' },
  review: { index: 2, label: 'Peer Review · scores anônimos', color: 'var(--apolo-accent)' },
  rebuttal: { index: 3, label: 'Réplicas', color: 'var(--apolo-warning)' },
  synthesis: { index: 3, label: 'Decisão do Chairman', color: CHAIRMAN_COLOR },
};
const PHASE_ORDER = ['opinions', 'review', 'rebuttal', 'synthesis'];

// localStorage keys — sobreviver reload
const LS_SELECTED = 'apolo:council:selected';
const LS_CHAIRMAN = 'apolo:council:chairman';
const LS_CUSTOM_AGENTS = 'apolo:council:custom-agents';
const LS_MODE = 'apolo:council:mode';
const LS_CONFIG_OPEN = 'apolo:council:config-open';

function loadSelected() {
  const fallback = COUNCIL_AGENTS.slice(0, MIN_AGENTS).map((a) => a.id);
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(LS_SELECTED);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const valid = parsed.filter((id) => typeof id === 'string');
      if (valid.length >= MIN_AGENTS) return valid.slice(0, MAX_AGENTS);
    }
  } catch { /* ignore parse errors */ }
  return fallback;
}

function loadCustomAgents() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LS_CUSTOM_AGENTS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch { return []; }
}

function loadChairman() {
  const fallback = COUNCIL_AGENTS[0]?.id || '';
  if (typeof window === 'undefined') return fallback;
  try { return window.localStorage.getItem(LS_CHAIRMAN) || fallback; } catch { return fallback; }
}

function loadMode() {
  if (typeof window === 'undefined') return 'rebuttal';
  try { return window.localStorage.getItem(LS_MODE) === 'essential' ? 'essential' : 'rebuttal'; } catch { return 'rebuttal'; }
}

function loadConfigOpen() {
  if (typeof window === 'undefined') return true;
  try { return window.localStorage.getItem(LS_CONFIG_OPEN) !== 'false'; } catch { return true; }
}

export default function CouncilView() {
  const [query, setQuery] = useState('');
  const [brief, setBrief] = useState({ context: '', constraints: '', criteria: '' });
  const [runInput, setRunInput] = useState(null);
  const [mode, setMode] = useState(loadMode);
  const [configOpen, setConfigOpen] = useState(loadConfigOpen);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeHistoryId, setActiveHistoryId] = useState(null);

  const {
    attachments, dragOver, uploadError, fileInputRef,
    removeAttachment, openFilePicker, dropZoneHandlers, fileInputHandlers,
  } = useFileAttachments();

  const [selectedIds, setSelectedIds] = useState(loadSelected);
  const [chairmanId, setChairmanId] = useState(loadChairman);
  const [customAgentIds, setCustomAgentIds] = useState(loadCustomAgents);

  const {
    phase, isRunning, opinionsByAgent, rebuttalsByAgent, agentErrors,
    synthesis, errorMessage, requestedChairmanId, effectiveChairmanId,
    scoresByAuthor, rankings, labelMapping, terminal, saved, progress,
    convoke: runDeliberation, cancel, reset,
  } = useCouncilDeliberation();

  const history = useCouncilHistory();

  // Persist selection + chairman + mode + config between reloads.
  useEffect(() => { try { window.localStorage.setItem(LS_SELECTED, JSON.stringify(selectedIds)); } catch { /* ignore */ } }, [selectedIds]);
  useEffect(() => { try { window.localStorage.setItem(LS_CHAIRMAN, chairmanId); } catch { /* ignore */ } }, [chairmanId]);
  useEffect(() => { try { window.localStorage.setItem(LS_CUSTOM_AGENTS, JSON.stringify(customAgentIds)); } catch { /* ignore */ } }, [customAgentIds]);
  useEffect(() => { try { window.localStorage.setItem(LS_MODE, mode); } catch { /* ignore */ } }, [mode]);
  useEffect(() => { try { window.localStorage.setItem(LS_CONFIG_OPEN, String(configOpen)); } catch { /* ignore */ } }, [configOpen]);

  const extraAgents = useMemo(
    () => customAgentIds.map((id) => agentFromRegistryModel(id, findModel(id))),
    [customAgentIds],
  );
  const effectiveRoster = useMemo(() => [...COUNCIL_AGENTS, ...extraAgents], [extraAgents]);

  const { allModels } = useUnifiedModels({ kind: 'text' });
  const availableToAdd = useMemo(() => {
    const rosterIds = new Set(effectiveRoster.map((a) => a.id));
    return allModels.filter((m) => !rosterIds.has(m.id));
  }, [effectiveRoster, allModels]);

  function addCustomAgent(modelId) {
    if (!modelId) return;
    setCustomAgentIds((prev) => (prev.includes(modelId) ? prev : [...prev, modelId]));
    setSelectedIds((prev) => (prev.includes(modelId) ? prev : [...prev, modelId]));
  }
  function removeCustomAgent(modelId) {
    setCustomAgentIds((prev) => prev.filter((id) => id !== modelId));
    setSelectedIds((prev) => prev.filter((id) => id !== modelId));
    if (chairmanId === modelId) setChairmanId('');
  }
  function toggleAgent(id) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    if (chairmanId === id && selectedIds.includes(id)) setChairmanId('');
  }
  function setChairman(id) { setChairmanId((prev) => (prev === id ? '' : id)); }
  function selectAll() { setSelectedIds(effectiveRoster.map((a) => a.id)); }
  function clearAll() { setSelectedIds([]); setChairmanId(''); }

  function convoke() {
    setActiveHistoryId(null);
    const fullQuery = [query, brief.context && `Contexto do projeto:\n${brief.context}`, brief.constraints && `Restrições:\n${brief.constraints}`, brief.criteria && `Critérios de decisão:\n${brief.criteria}`].filter(Boolean).join('\n\n');
    setRunInput({ query: fullQuery, agents: [...selectedIds], chairman: chairmanId, mode });
    runDeliberation({ query: fullQuery, agents: selectedIds, chairman: chairmanId, attachments, mode });
  }

  // Reuse a prior deliberation: fetch the detail (returns the normalized row so
  // we never read stale hook state), then restore the composer/config.
  async function reuse(id) {
    const cached = history.openDetail?.id === id ? history.openDetail : null;
    const row = cached || await history.openDeliberation(id);
    const payload = row ? reusePayloadFromHistory(row) : null;
    if (!payload) return;
    if (isRunning) return;
    reset();
    setRunInput(null);
    setBrief({ context: '', constraints: '', criteria: '' });
    setActiveHistoryId(null);
    setQuery(payload.query);
    setMode(payload.mode);
    // Ensure reused agents are in the roster + selected.
    const known = new Set(effectiveRoster.map((a) => a.id));
    const missing = payload.agents.filter((agentId) => !known.has(agentId));
    if (missing.length) setCustomAgentIds((prev) => [...new Set([...prev, ...missing])]);
    setSelectedIds(payload.agents);
    setChairmanId(payload.chairman);
    setHistoryOpen(false);
  }

  // Export/reuse over the CURRENT live run.
  const liveDeliberation = useMemo(() => ({
    ...(runInput || { query, agents: selectedIds, chairman: chairmanId, mode }),
    effectiveChairman: effectiveChairmanId, status: terminal?.status || 'complete',
    saved, opinionsByAgent, rankings, labelMapping, rebuttalsByAgent, synthesis,
    costKnown: terminal?.costKnown, knownCostUsd: terminal?.knownCostUsd, totalCostUsd: terminal?.totalCostUsd,
  }), [runInput, query, selectedIds, chairmanId, effectiveChairmanId, terminal, saved, mode, opinionsByAgent, rankings, labelMapping, rebuttalsByAgent, synthesis]);
  const liveMarkdown = useMemo(() => deliberationToMarkdown(liveDeliberation), [liveDeliberation]);

  function exportMarkdown() { downloadCouncilMarkdown(liveMarkdown); }

  const chairman = useMemo(() => (chairmanId ? getAgent(chairmanId, extraAgents) : null), [chairmanId, extraAgents]);
  const requestedChairman = requestedChairmanId ? getAgent(requestedChairmanId, extraAgents) : null;
  const effectiveChairman = effectiveChairmanId ? getAgent(effectiveChairmanId, extraAgents) : null;
  const canConvoke = query.trim().length > 0 && selectedIds.length >= MIN_AGENTS && selectedIds.length <= MAX_AGENTS && selectedIds.includes(chairmanId) && phase === 'idle';

  // Which phases show: any phase whose index <= current, plus rebuttal only in
  // rebuttal mode. `done` shows everything that has content.
  const currentPhaseIdx = PHASE_ORDER.indexOf(phase);
  function phaseVisible(name) {
    if (phase === 'done') {
      if (name === 'synthesis') return true;
      if (name === 'rebuttal') return mode === 'rebuttal';
      return true;
    }
    if (phase === 'idle') return false;
    const idx = PHASE_ORDER.indexOf(name);
    if (name === 'rebuttal' && mode !== 'rebuttal') return false;
    if (phase === 'synthesis' && name === 'rebuttal' && mode !== 'rebuttal') return false;
    return idx <= currentPhaseIdx;
  }

  const historyPanel = (
    <CouncilHistoryList
      history={history}
      activeId={activeHistoryId}
      onReuse={reuse}
      onOpenDetail={(id) => { setActiveHistoryId(id); setHistoryOpen(false); }}
    />
  );

  return (
    <main id="tabpanel-council" role="tabpanel" aria-labelledby="tab-council" className="flex h-full flex-1 flex-col overflow-hidden bg-apolo-bg">
      <header className="border-b border-apolo-borderSubtle px-4 py-4 lg:px-6 lg:py-5">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-apolo-textPrimary lg:text-2xl">
              <span aria-hidden="true" className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full lg:h-10 lg:w-10" style={{ background: `radial-gradient(circle, ${CHAIRMAN_COLOR}28 0%, ${CHAIRMAN_COLOR}10 50%, transparent 80%)`, boxShadow: `0 0 24px ${CHAIRMAN_GLOW}, inset 0 0 0 1px ${CHAIRMAN_COLOR}33` }}>
                <Crown size={18} className="lg:h-[22px] lg:w-[22px]" style={{ color: CHAIRMAN_COLOR }} aria-hidden="true" />
              </span>
              The Council
            </h1>
            <p className="text-xs text-apolo-textTertiary">Múltiplas IAs deliberam, revisam e replicam; o Chairman consolida a decisão final.</p>
          </div>
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2.5 py-1.5 text-[11px] text-apolo-textSecondary transition hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing lg:hidden"
            aria-label="Abrir histórico de deliberações"
          >
            <HistoryIcon size={12} aria-hidden="true" /> Histórico
          </button>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-6xl flex-1 gap-5 overflow-hidden px-3 py-4 lg:gap-6 lg:px-6 lg:py-8">
        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col gap-5 overflow-y-auto lg:gap-8">
          {errorMessage && (
            <div role="alert" className="rounded-lg border border-apolo-error/30 bg-apolo-errorBg px-3 py-2 text-xs text-apolo-error">
              {errorMessage}
            </div>
          )}

          {activeHistoryId && <CouncilHistoryDetail detail={history.openDetail} loading={history.detailLoading} onClose={() => setActiveHistoryId(null)} onReuse={reuse} />}

          <CouncilComposer
            brief={brief}
            onBriefChange={setBrief}
            query={query}
            onQueryChange={setQuery}
            attachments={attachments}
            dragOver={dragOver}
            uploadError={uploadError}
            removeAttachment={removeAttachment}
            openFilePicker={openFilePicker}
            dropZoneHandlers={dropZoneHandlers}
            fileInputRef={fileInputRef}
            fileInputHandlers={fileInputHandlers}
            mode={mode}
            onModeChange={setMode}
            isRunning={isRunning}
            canConvoke={canConvoke}
            onConvoke={convoke}
            onCancel={cancel}
            onReset={() => { reset(); setRunInput(null); }}
            phase={phase}
          />

          {/* Collapsible config: models + chairman */}
          <section aria-label="Configuração do conselho" className="rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1/60">
            <button
              type="button"
              onClick={() => setConfigOpen((v) => !v)}
              aria-expanded={configOpen}
              className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing lg:px-4"
            >
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-apolo-textPrimary">
                <Settings2 size={12} aria-hidden="true" /> Configuração {' '}
                <span className="font-normal text-apolo-textTertiary">
                  · {selectedIds.length} conselheiro(s) · Chairman: {chairman ? chairman.name : '—'}
                </span>
              </span>
              <span className="text-[10px] text-apolo-textTertiary">{configOpen ? 'Recolher' : 'Expandir'}</span>
            </button>
            {configOpen && (
              <div className="flex flex-col gap-3 border-t border-apolo-borderSubtle px-3 py-3 lg:px-4">
                <CouncilModelPicker
                  roster={effectiveRoster}
                  selectedIds={selectedIds}
                  chairmanId={chairmanId}
                  onToggle={toggleAgent}
                  onChairman={setChairman}
                  onSelectAll={selectAll}
                  onClearAll={clearAll}
                  onRemoveCustom={removeCustomAgent}
                  onAddModel={addCustomAgent}
                  availableToAdd={availableToAdd}
                  disabled={isRunning}
                />
                <p className="text-[11px] text-apolo-textTertiary">
                  {chairman ? (<>Chairman: <strong style={{ color: chairman.color }}>{chairman.name}</strong></>) : 'Nenhum Chairman designado — clique na coroa de um conselheiro.'}
                </p>
              </div>
            )}
          </section>

          <CouncilStatus terminal={terminal} markdown={liveMarkdown} onExport={exportMarkdown} />

          {/* PHASES */}
          <AnimatePresence>
            {phaseVisible('opinions') && (
              <motion.section key="opinions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col">
                <SectionHeader index={1} label={PHASE_META.opinions.label} color={PHASE_META.opinions.color} active={phase === 'opinions'} done={currentPhaseIdx > 0 || phase === 'done'} count={selectedIds.filter((id) => opinionsByAgent[id]).length} />
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {selectedIds.map((id) => {
                    const a = getAgent(id, extraAgents);
                    if (!a) return null;
                    return (
                      <ExpandableOutput
                        key={id}
                        title={a.name}
                        subtitle={a.provider + (id === chairmanId ? ' · Chairman' : '')}
                        accentColor={a.color}
                        content={opinionsByAgent[id]}
                        errorMessage={agentErrors[id]}
                        streaming={phase === 'opinions' && !progress.opinions?.find((p) => p.agentId === id)?.done && !agentErrors[id]}
                      />
                    );
                  })}
                </div>
              </motion.section>
            )}

            {phaseVisible('review') && (
              <motion.section key="review" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col">
                <SectionHeader index={2} label={PHASE_META.review.label} color={PHASE_META.review.color} active={phase === 'review'} done={currentPhaseIdx > 1 || phase === 'done'} />
                <div className="flex flex-col gap-3">
                  <ReviewMatrix selectedIds={selectedIds} scoresByAuthor={scoresByAuthor} extraAgents={extraAgents} />
                  <ReviewComparison scoresByAuthor={scoresByAuthor} rankings={rankings} extraAgents={extraAgents} />
                </div>
              </motion.section>
            )}

            {phaseVisible('rebuttal') && (
              <motion.section key="rebuttal" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col">
                <SectionHeader index={3} label={PHASE_META.rebuttal.label} color={PHASE_META.rebuttal.color} active={phase === 'rebuttal'} done={currentPhaseIdx > 2 || phase === 'done'} count={selectedIds.filter((id) => rebuttalsByAgent[id]).length} />
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {selectedIds.map((id) => {
                    const a = getAgent(id, extraAgents);
                    if (!a) return null;
                    return (
                      <ExpandableOutput
                        key={id}
                        title={a.name}
                        subtitle={`${a.provider} · réplica`}
                        accentColor={a.color}
                        content={rebuttalsByAgent[id]}
                        errorMessage={agentErrors[id]}
                        streaming={phase === 'rebuttal' && !progress.rebuttal?.find((p) => p.agentId === id)?.done && !agentErrors[id]}
                      />
                    );
                  })}
                </div>
              </motion.section>
            )}

            {phaseVisible('synthesis') && (
              <motion.section key="synthesis" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col">
                <SectionHeader index={mode === 'rebuttal' ? 4 : 3} label={PHASE_META.synthesis.label} color={CHAIRMAN_COLOR} active={phase === 'synthesis'} done={phase === 'done'} />
                <SynthesisCard chairman={effectiveChairman} requestedChairman={requestedChairman} synthesis={synthesis} isRunning={phase === 'synthesis'} />
              </motion.section>
            )}
          </AnimatePresence>

          {/* Per-agent progress for the active phase */}
          {isRunning && (phase === 'opinions' || phase === 'review' || phase === 'rebuttal') && progress[phase]?.length > 0 && (
            <section aria-live="polite" aria-label="Progresso dos conselheiros" className="flex flex-wrap items-center gap-2 text-[10.5px] text-apolo-textTertiary">
              {progress[phase].map((p) => {
                const a = getAgent(p.agentId, extraAgents);
                return (
                  <span key={p.agentId} className="inline-flex items-center gap-1">
                    <span aria-hidden="true" className="inline-flex h-3 w-3 items-center justify-center rounded-full" style={{ backgroundColor: a?.color || 'var(--apolo-textTertiary)' }} />
                    {a?.name || p.agentId}
                    <span className={p.errored ? 'text-apolo-error' : p.done ? 'text-apolo-success' : ''}>{p.errored ? 'falhou' : p.done ? 'ok' : '…'}</span>
                  </span>
                );
              })}
            </section>
          )}

          {/* IDLE empty state */}
          {phase === 'idle' && (
            <section aria-label="Como funciona" className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                { n: 1, t: 'Primeiras opiniões', d: 'Cada conselheiro responde em paralelo, sem ver o que os outros disseram.', c: 'var(--apolo-accent)' },
                { n: 2, t: 'Peer Review', d: 'Os conselheiros revisam as opiniões anônimas uns dos outros e atribuem scores.', c: 'var(--apolo-accent)' },
                { n: mode === 'rebuttal' ? 3 : 3, t: mode === 'rebuttal' ? 'Réplicas' : 'Chairman decide', d: mode === 'rebuttal' ? 'Cada conselheiro diz o que mudou à luz das críticas.' : 'O Chairman consolida as melhores ideias numa síntese final.', c: mode === 'rebuttal' ? 'var(--apolo-warning)' : CHAIRMAN_COLOR },
              ].map((step) => (
                <div key={step.t} className="flex flex-col gap-2 rounded-xl border border-apolo-borderSubtle bg-apolo-surface1/60 p-4">
                  <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold" style={{ backgroundColor: `color-mix(in srgb, ${step.c} 10%, transparent)`, color: step.c, border: `1px solid color-mix(in srgb, ${step.c} 25%, transparent)` }}>{step.n}</span>
                  <h3 className="text-xs font-semibold text-apolo-textPrimary">{step.t}</h3>
                  <p className="text-[11px] leading-relaxed text-apolo-textTertiary">{step.d}</p>
                </div>
              ))}
            </section>
          )}
        </div>

        {/* History sidebar (desktop) */}
        <aside className="order-first hidden w-72 shrink-0 overflow-hidden rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1/60 lg:flex lg:flex-col" aria-label="Histórico de deliberações">
          {historyPanel}
        </aside>
      </div>

      {/* History drawer (mobile) */}
      <MobileDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} title="Histórico">
        {historyPanel}
      </MobileDrawer>
    </main>
  );
}

// Chairman synthesis card — reuses the shared sanitized Markdown renderer.
function SynthesisCard({ chairman, requestedChairman, synthesis, isRunning }) {
  const fallback = chairman && requestedChairman && chairman.id !== requestedChairman.id;
  return (
    <article className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-apolo-surface1 via-apolo-surface1 to-apolo-surface2 p-5" style={{ borderColor: `${CHAIRMAN_COLOR}66`, boxShadow: `0 0 32px ${CHAIRMAN_GLOW}, inset 0 0 0 1px ${CHAIRMAN_COLOR}22` }}>
      <header className="mb-3 flex items-center gap-2">
        <span aria-hidden="true" className="inline-flex h-8 w-8 items-center justify-center rounded-full" style={{ backgroundColor: `${CHAIRMAN_COLOR}20`, boxShadow: `0 0 16px ${CHAIRMAN_GLOW}` }}>
          <Crown size={14} style={{ color: CHAIRMAN_COLOR }} aria-hidden="true" />
        </span>
        <div className="flex flex-col leading-tight">
          <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: CHAIRMAN_COLOR }}>
            <Star size={10} aria-hidden="true" /> Síntese do Chairman
          </span>
          {chairman && <span className="text-[10px] text-apolo-textTertiary">{synthesis ? 'decidido por' : 'síntese de'} {chairman.name}</span>}
          {fallback && <span className="text-[10px] text-apolo-textTertiary">{requestedChairman.name} foi substituído após falhar na primeira fase.</span>}
        </div>
      </header>
      {synthesis ? (
        <MarkdownMessage content={synthesis} />
      ) : (
        <p className="text-sm text-apolo-textTertiary">{isRunning ? 'Sintetizando…' : 'Nenhuma síntese foi concluída.'}</p>
      )}
    </article>
  );
}
